import { useMemo } from 'react';
import type { BuildState } from '@/types';
import { getShaft } from '@/lib/calculations';
import { NOCKS } from '@/data/nocks';
import { POINTS } from '@/data/points';
import { VANES } from '@/data/vanes';
import {
  classifyVane,
  classifyPoint,
  classifyNock,
  parseInches,
  fletchPaths,
  pointShapes,
  nockShapes,
  pointCutDiameterIn,
  VANE_HEIGHT_IN,
  POINT_LENGTH_IN,
} from '@/lib/arrowProfiles';

/** Shaft finish presets → [dark, mid, darkest] gradient stops. */
export type ShaftFinish = 'carbon' | 'black' | 'white' | 'olive';
export const SHAFT_FINISHES: Record<ShaftFinish, readonly [string, string, string]> = {
  carbon: ['#3f3f46', '#52525b', '#18181b'],
  black: ['#27272a', '#3f3f46', '#09090b'],
  white: ['#d4d4d8', '#fafafa', '#a1a1aa'],
  olive: ['#3f3f2a', '#56602f', '#1a1a0f'],
};

/** User-tunable cosmetic finish for Render mode. Nothing here feeds ballistics. */
export interface Appearance {
  shaftFinish: ShaftFinish;
  vaneColor: string; // hens
  cockVaneColor: string;
  nockColor: string;
  wrapColor: string;
}

/** Default Sparrow palette — carbon shaft, sky hens, ember cock vane. */
export const DEFAULT_APPEARANCE: Appearance = {
  shaftFinish: 'carbon',
  vaneColor: '#38bdf8',
  cockVaneColor: '#f59e0b',
  nockColor: '#e4e4e7',
  wrapColor: '#3f3f46',
};

interface RealisticArrowProps {
  state: BuildState;
  finish?: Appearance;
}

const VIEW_W = 800;
const VIEW_H = 156;
const PAD_L = 54;
const PAD_R = 72;
const ARROW_Y = 80;

function parseOD(od: string): number {
  const v = parseFloat(od.replace(/[^0-9.]/g, ''));
  return Number.isFinite(v) ? v : 0.234;
}

/**
 * True-to-life "Render" of the current build. Every component is drawn at its
 * real proportions: the shaft spans the arrow's stated length, vanes sit at
 * their real length + height, and the broadhead shows the right blade count at
 * its real cut diameter — all scaled by one shared pixels-per-inch so the
 * picture is dimensionally honest. Pure vector + gradient shading (no photos,
 * no manufacturer IP). The Blueprint view stays one toggle away — that's where
 * the brain lives.
 */
/** Which components the glyph draws — lets the Wizard reveal them progressively. */
export interface GlyphVisibility {
  nock?: boolean;
  shaft?: boolean;
  vane?: boolean;
  point?: boolean;
  wrap?: boolean;
}

function ArrowGlyph({
  state,
  finish,
  show,
}: {
  state: BuildState;
  finish: Appearance;
  show?: GlyphVisibility;
}) {
  const vis = (k: keyof GlyphVisibility): boolean => show?.[k] !== false;
  const L = state.length;
  const shaft = getShaft(state);
  const nock = NOCKS[state.nockIdx] ?? NOCKS[0];
  const point = POINTS[state.pointPreset] ?? POINTS[0];
  const vane = VANES[state.vaneIdx];

  const usable = VIEW_W - PAD_L - PAD_R;
  const pxPerInch = usable / L;
  const xAt = useMemo(() => (inches: number) => PAD_L + inches * pxPerInch, [pxPerInch]);

  const od = parseOD(shaft.od);
  const shaftHalf = Math.max(3, (od * pxPerInch) / 2);

  // Vane geometry (true scale)
  const vProfile = classifyVane(vane);
  const vaneLenIn = parseInches(vane?.length, 2.0);
  const vaneH = VANE_HEIGHT_IN[vProfile] * pxPerInch;
  const vRear = xAt(state.vaneBackPosition);
  const vFront = xAt(state.vaneBackPosition + vaneLenIn);

  // Point geometry (true scale)
  const pProfile = classifyPoint(point);
  const pointLenIn = POINT_LENGTH_IN[pProfile];
  const tipX = xAt(L) + pointLenIn * pxPerInch;
  const cutIn = pointCutDiameterIn(point, pProfile);
  const halfSpread = Math.min(ARROW_Y - 8, (cutIn / 2) * pxPerInch);
  const shapes = pointShapes(pProfile, xAt(L), tipX, ARROW_Y, shaftHalf, halfSpread);

  const [s0, s1, s2] = SHAFT_FINISHES[finish.shaftFinish];

  return (
    <>
      <defs>
        <linearGradient id="ra-shaft" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={s1} />
          <stop offset="22%" stopColor="#ffffff" stopOpacity="0.85" />
          <stop offset="24%" stopColor={s0} />
          <stop offset="55%" stopColor={s1} />
          <stop offset="100%" stopColor={s2} />
        </linearGradient>
        <linearGradient id="ra-vane" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lighten(finish.vaneColor)} />
          <stop offset="45%" stopColor={finish.vaneColor} />
          <stop offset="100%" stopColor={darken(finish.vaneColor)} />
        </linearGradient>
        <linearGradient id="ra-cock" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor={lighten(finish.cockVaneColor)} />
          <stop offset="45%" stopColor={finish.cockVaneColor} />
          <stop offset="100%" stopColor={darken(finish.cockVaneColor)} />
        </linearGradient>
        <linearGradient id="ra-blade" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#6b7280" />
          <stop offset="42%" stopColor="#f9fafb" />
          <stop offset="56%" stopColor="#cbd5e1" />
          <stop offset="100%" stopColor="#475569" />
        </linearGradient>
        <linearGradient id="ra-ferrule" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e5e7eb" />
          <stop offset="50%" stopColor="#6b7280" />
          <stop offset="100%" stopColor="#111827" />
        </linearGradient>
        <linearGradient id="ra-wrap" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lighten(finish.wrapColor)} />
          <stop offset="40%" stopColor={finish.wrapColor} />
          <stop offset="100%" stopColor={darken(finish.wrapColor)} />
        </linearGradient>
        <radialGradient id="ra-shadow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#000000" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="ra-glow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#fb7185" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#fb7185" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Ground shadow */}
      <ellipse cx={VIEW_W / 2} cy={ARROW_Y + shaftHalf + 22} rx={(xAt(L) - xAt(0)) / 2.05} ry={6} fill="url(#ra-shadow)" />

      {/* ── Nock (rear) — true profile by type ── */}
      {vis('nock') && (() => {
        const nProfile = classifyNock(nock);
        const color = nock.lighted ? '#e11d48' : finish.nockColor;
        const ns = nockShapes(nProfile, xAt(0) + 1, ARROW_Y, shaftHalf);
        return (
          <g strokeLinejoin="round" strokeLinecap="round">
            {nock.lighted && <circle cx={xAt(0) - shaftHalf * 4} cy={ARROW_Y} r={shaftHalf * 2.4} fill="url(#ra-glow)" />}
            {ns.map((sh, i) => (
              <path
                key={i}
                d={sh.d}
                fill={sh.kind === 'led' ? '#f43f5e' : sh.kind === 'slot' ? '#18181b' : sh.kind === 'detail' ? 'none' : color}
                stroke={sh.kind === 'detail' ? '#3f3f46' : '#27272a'}
                strokeWidth={0.5}
                opacity={sh.kind === 'led' ? 0.9 : 1}
              />
            ))}
          </g>
        );
      })()}

      {/* ── Shaft ── */}
      {vis('shaft') && (
        <rect x={xAt(0)} y={ARROW_Y - shaftHalf} width={xAt(L) - xAt(0)} height={shaftHalf * 2} fill="url(#ra-shaft)" rx={shaftHalf} />
      )}

      {/* ── Wrap (cosmetic) ── */}
      {state.wrap && vis('wrap') && (() => {
        const wx = xAt(0.3);
        const ww = xAt(0.3 + state.wrapLen) - wx;
        return <rect x={wx} y={ARROW_Y - shaftHalf - 0.5} width={ww} height={shaftHalf * 2 + 1} fill="url(#ra-wrap)" rx={1.5} />;
      })()}

      {/* ── Fletching — realistic 3/4-fletch: tall cock vane up, splayed hens ── */}
      {vis('vane') && (() => {
        const fl = fletchPaths(vProfile, vRear, vFront, ARROW_Y, shaftHalf, vaneH, state.vaneCount);
        const fill = (k: string) =>
          k === 'cock' ? 'url(#ra-cock)' : k === 'hen-far' ? darken(finish.vaneColor) : 'url(#ra-vane)';
        const stroke = (k: string) => (k === 'cock' ? darken(finish.cockVaneColor) : darken(finish.vaneColor));
        return (
          <g strokeLinejoin="round" strokeLinecap="round">
            {/* base rail — the vane base/tape seated on the shaft */}
            <rect x={vRear} y={ARROW_Y - shaftHalf - 1} width={vFront - vRear} height={2} fill={darken(finish.vaneColor)} opacity={0.7} rx={1} />
            {fl.map((p, i) => (
              <path key={i} d={p.d} fill={fill(p.kind)} stroke={stroke(p.kind)} strokeWidth={0.7} opacity={p.kind === 'hen-far' ? 0.92 : 1} />
            ))}
          </g>
        );
      })()}

      {/* ── Point / broadhead (true length + cut diameter) ── */}
      {vis('point') && shapes.map((sh, i) => (
        <path
          key={i}
          d={sh.d}
          fill={sh.kind === 'wire' ? 'none' : sh.kind === 'blade' || sh.kind === 'bleeder' ? 'url(#ra-blade)' : 'url(#ra-ferrule)'}
          stroke={sh.kind === 'wire' ? '#9ca3af' : sh.kind === 'blade' || sh.kind === 'bleeder' ? '#94a3b8' : '#334155'}
          strokeWidth={sh.kind === 'wire' ? 1.2 : 0.4}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </>
  );
}

/** Standalone render — wraps the glyph in a sized, accessible SVG. */
export function RealisticArrow({ state, finish = DEFAULT_APPEARANCE }: RealisticArrowProps) {
  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      className="w-full h-auto min-w-[640px]"
      role="img"
      aria-label={`True-scale render of a ${state.length.toFixed(1)}-inch arrow build`}
    >
      <ArrowGlyph state={state} finish={finish} />
    </svg>
  );
}

/** Internal view geometry — exported so the share card can size its embed. */
export const RENDER_VIEW = { width: VIEW_W, height: VIEW_H } as const;
export { ArrowGlyph };

// ── Tiny hex shade helpers (cosmetic only) ──────────────────────────
function clamp(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}
function shade(hex: string, amt: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = clamp(((n >> 16) & 255) + amt);
  const g = clamp(((n >> 8) & 255) + amt);
  const b = clamp((n & 255) + amt);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}
function lighten(hex: string): string {
  return shade(hex, 48);
}
function darken(hex: string): string {
  return shade(hex, -54);
}
